<template>
    <CRow>
      <!-- ================= Edit Committee Modal ================= -->
      <CModal size="lg" title="Edit Committee Member" :show.sync="editModal" :close-on-backdrop="false" color="primary">
        <div class="p-3">
          <div v-if="formLoading" class="text-center py-4">Loading...</div>
          <template v-else>
            <!-- Basic member details only -->
            <div v-if="selectedMember" class="mb-3 p-2 border rounded bg-light">
              <div><strong>Name:</strong> {{ selectedMember.name || '-' }}</div>
              <div><strong>Email:</strong> {{ selectedMember.email || '-' }}</div>
              <div><strong>Mobile No:</strong> {{ selectedMember.mobileNo || '-' }}</div>
              <div><strong>Batch:</strong> {{ selectedMember.batch || '-' }}</div>
            </div>
  
            <div class="form-group">
              <label>Year <span class="text-danger">*</span></label>
              <select v-model="committeeForm.year" class="form-control">
                <option :value="null" disabled>Select Year</option>
                <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
  
            <div class="form-group">
              <label>Committee Title <span class="text-danger">*</span></label>
              <select v-model="committeeForm.titleId" class="form-control" :disabled="titlesLoading">
                <option :value="null" disabled>{{ titlesLoading ? 'Loading...' : 'Select Title' }}</option>
                <option v-for="t in committeeTitles" :key="t.id" :value="t.id">{{ t.title }}</option>
              </select>
            </div>
  
            <div class="form-group">
              <label>Category</label>
              <input
                v-model="committeeForm.category"
                type="text"
                class="form-control"
                maxlength="191"
                placeholder="Enter category (optional)"
              />
            </div>
  
            <small v-if="committeeError" class="text-danger">{{ committeeError }}</small>
          </template>
        </div>
        <template #footer>
          <CButton color="secondary" @click="editModal = false">Cancel</CButton>
          <CButton color="primary" :disabled="saving || formLoading" @click="updateCommittee">Update</CButton>
        </template>
      </CModal>
  
      <!-- ================= View Committee Modal ================= -->
      <CModal size="lg" title="Committee Member" :show.sync="viewModal" color="primary">
        <div class="p-3">
          <div v-if="viewLoading" class="text-center py-4">Loading...</div>
          <div v-else-if="viewData">
            <div v-if="viewData.member" class="d-flex align-items-center mb-3">
              <img
                v-if="viewData.member.avatar"
                :src="viewData.member.avatar"
                class="rounded-circle mr-3"
                width="64"
                height="64"
                style="object-fit: cover"
                alt=""
              />
              <div>
                <h5 class="mb-1">{{ viewData.member.name || '-' }}</h5>
                <CBadge :color="statusInfo(viewData.member.status).color">
                  {{ statusInfo(viewData.member.status).label }}
                </CBadge>
              </div>
            </div>
            <table class="table table-sm table-borderless mb-0">
              <tbody>
                <tr><th style="width: 160px">Committee Title</th><td>{{ viewData.title || '-' }}</td></tr>
                <tr><th>Category</th><td>{{ viewData.category || '-' }}</td></tr>
                <tr><th>Year</th><td>{{ viewData.year || '-' }}</td></tr>
                <tr><th>Email</th><td>{{ (viewData.member && viewData.member.email) || '-' }}</td></tr>
                <tr><th>Mobile No</th><td>{{ (viewData.member && viewData.member.mobileNo) || '-' }}</td></tr>
                <tr><th>Batch</th><td>{{ (viewData.member && viewData.member.batch) || '-' }}</td></tr>
                <tr><th>Added On</th><td>{{ viewData.createdAt || '-' }}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
        <template #footer>
          <CButton color="secondary" @click="viewModal = false">Close</CButton>
        </template>
      </CModal>
  
      <!-- ================= Delete Confirm Modal ================= -->
      <CModal title="Delete Committee Member" :show.sync="deleteModal" color="danger">
        <p class="mb-0">
          Remove <strong>{{ deleteRow && deleteRow.name }}</strong>
          as <strong>{{ deleteRow && deleteRow.title }}</strong> ({{ deleteRow && deleteRow.year }})?
        </p>
        <template #footer>
          <CButton color="secondary" @click="deleteModal = false">Cancel</CButton>
          <CButton color="danger" :disabled="saving" @click="deleteCommittee">Delete</CButton>
        </template>
      </CModal>
  
      <CCol col="12">
        <CCard>
          <CCardBody>
            <CAlert :color="messageColor" closeButton :show.sync="dismissCountDownS" fade>
              {{ alertMessage }}
            </CAlert>
  
            <v-server-table :columns="columns" :url="apiUrl" :options="options" ref="committeeTable">
              <template #category="data">{{ data.row.category || '-' }}</template>
              <template #actions="data">
                <CButton color="info" size="sm" class="mr-1 mb-1" @click="openView(data.row)">View</CButton>
                <CButton color="primary" size="sm" class="mr-1 mb-1" @click="openEdit(data.row)">Edit</CButton>
                <CButton color="danger" size="sm" class="mb-1" @click="openDelete(data.row)">Delete</CButton>
              </template>
            </v-server-table>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </template>
  
  <script>
  import Vue from "vue";
  import {Mixin} from "/src/mixins/index.js";
  import {ServerTable} from "vue-tables-2";
  Vue.use(ServerTable, {}, false);
  
  export default {
    name: "Committee",
    mixins: [Mixin],
    data() {
      return {
        messageColor: "success",
        dismissCountDownS: 0,
        alertMessage: "",
        saving: false,
  
        committeeTitles: [],
        titlesLoading: false,
        committeeError: "",
        committeeForm: {id: null, memberId: null, year: null, titleId: null, category: ""},
  
        apiUrl: "",
        columns: ["name", "title", "category", "year", "batch", "actions"],
        options: {
          headings: {
            name: "Name",
            title: "Committee Title",
            category: "Category",
            year: "Year",
            batch: "Batch",
            actions: "Action",
          },
          sortable: [],
          filterable: ["name", "title", "category"],
        },
  
        editModal: false,
        formLoading: false,
        selectedMember: null,
  
        viewModal: false,
        viewLoading: false,
        viewData: null,
  
        deleteModal: false,
        deleteRow: null,
      };
    },
    computed: {
      yearOptions() {
        const current = new Date().getFullYear();
        const years = [];
        for (let y = current + 1; y >= current - 10; y--) years.push(y);
        // Keep an older saved year selectable when editing
        const saved = Number(this.committeeForm.year);
        if (saved && !years.includes(saved)) years.push(saved);
        return years;
      },
    },
    created() {
      this.apiUrl = "/institute-chain/list-committee/" + this.$route.params.id;
    },
    methods: {
      /* ---------- Helpers ---------- */
      statusInfo(status) {
        const s = String(status === null || status === undefined ? "" : status).toLowerCase();
        if (s === "1" || s === "approved") return {label: "Approved", color: "success"};
        if (s === "0" || s === "pending") return {label: "Pending", color: "warning"};
        if (s === "2" || s === "blocked") return {label: "Blocked", color: "danger"};
        if (s === "3" || s === "removed") return {label: "Removed", color: "secondary"};
        if (s === "4" || s === "rejected") return {label: "Rejected", color: "danger"};
        return {label: status || "-", color: "info"};
      },
      showAlert(message, color = "success") {
        this.alertMessage = message;
        this.messageColor = color;
        this.dismissCountDownS = 10;
      },
      errorMessage(error, fallback) {
        return (error.response && error.response.data && error.response.data.message) || fallback;
      },
      /** Error only when the API says so; a plain { message } is a success text */
      isErrorResponse(response) {
        const d = response.data || {};
        return d.status === false || d.success === false || !!d.error;
      },
      successText(response, fallback) {
        const d = response.data || {};
        if (typeof d.success === "string") return d.success;
        if (typeof d.message === "string") return d.message;
        return fallback;
      },
      /** Empty / whitespace -> null */
      cleanCategory(value) {
        const v = (value === null || value === undefined ? "" : String(value)).trim();
        return v === "" ? null : v;
      },
      fetchCommitteeTitles() {
        if (this.committeeTitles.length || this.titlesLoading) return;
        this.titlesLoading = true;
        axios
          .get("/institute-chain/title")
          .then((response) => {
            const raw = response.data && (response.data.data || response.data);
            this.committeeTitles = (Array.isArray(raw) ? raw : []).map((t) => ({
              id: t.id,
              title: t.title || t.name,
            }));
            if (!this.committeeTitles.length) this.committeeError = "No committee titles found.";
          })
          .catch(() => (this.committeeError = "Could not load committee titles."))
          .finally(() => (this.titlesLoading = false));
      },
  
      refreshTable() {
        if (this.$refs.committeeTable) this.$refs.committeeTable.refresh();
      },
      getDetail(id) {
        return axios.post("/institute-chain/view-committee", {id});
      },
  
      /* ---------- View ---------- */
      openView(row) {
        this.viewData = null;
        this.viewModal = true;
        this.viewLoading = true;
        this.getDetail(row.id)
          .then((response) => (this.viewData = response.data.data || null))
          .catch((error) => {
            this.viewModal = false;
            this.showAlert(this.errorMessage(error, "Could not load committee member"), "danger");
          })
          .finally(() => (this.viewLoading = false));
      },
  
      /* ---------- Edit ---------- */
      openEdit(row) {
        this.committeeError = "";
        this.selectedMember = {name: row.name, email: row.email, mobileNo: row.mobileNo, batch: row.batch};
        this.committeeForm = {
          id: row.id,
          memberId: row.memberId,
          year: Number(row.year),
          titleId: row.titleId,
          category: row.category || "",
        };
        this.editModal = true;
        this.fetchCommitteeTitles();
  
        // Load the latest saved values
        this.formLoading = true;
        this.getDetail(row.id)
          .then((response) => {
            const d = response.data.data || {};
            this.committeeForm.year = Number(d.year) || this.committeeForm.year;
            this.committeeForm.titleId = d.titleId || this.committeeForm.titleId;
            if (d.category !== undefined) this.committeeForm.category = d.category || "";
            if (d.member) {
              this.committeeForm.memberId = d.member.id;
              this.selectedMember = d.member;
            }
          })
          .catch((error) => (this.committeeError = this.errorMessage(error, "Could not load committee member")))
          .finally(() => (this.formLoading = false));
      },
      updateCommittee() {
        const f = this.committeeForm;
        if (!f.year || !f.titleId) {
          this.committeeError = "Please select year and committee title.";
          return;
        }
        this.committeeError = "";
        this.saving = true;
        axios
          .post("/institute-chain/update-committee/" + f.id, {
            collegeAlumniNetworkId: f.memberId,
            titleId: f.titleId,
            year: f.year,
            category: this.cleanCategory(f.category),
          })
          .then((response) => {
            if (this.isErrorResponse(response)) {
              this.committeeError = this.successText(response, "Failed to update committee member");
              return;
            }
            // Done: close the modal and reload the list
            this.editModal = false;
            this.showAlert(this.successText(response, "Committee member updated"));
            this.refreshTable();
          })
          .catch((error) => (this.committeeError = this.errorMessage(error, "Failed to update committee member")))
          .finally(() => (this.saving = false));
      },
  
      /* ---------- Delete ---------- */
      openDelete(row) {
        this.deleteRow = row;
        this.deleteModal = true;
      },
      deleteCommittee() {
        this.saving = true;
        axios
          .delete("/institute-chain/committee/" + this.deleteRow.id)
          .then((response) => {
            this.deleteModal = false;
            if (this.isErrorResponse(response)) {
              this.showAlert(this.successText(response, "Failed to delete committee member"), "danger");
              return;
            }
            this.showAlert(this.successText(response, "Committee member deleted"));
            this.refreshTable();
          })
          .catch((error) => {
            this.deleteModal = false;
            this.showAlert(this.errorMessage(error, "Failed to delete committee member"), "danger");
          })
          .finally(() => (this.saving = false));
      },
    },
  };
  </script>